import { Stack, Card, Box, Typography, Chip, Tooltip, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { BaseTable } from "../BaseTable/BaseTable";
import { useSelector, useDispatch } from "react-redux";
import { paymentGatewayListColumns } from "./PaymentGateway.constants";
import Page from "../Page";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { alertSlice } from "src/store/alert/alert.slice";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { Icon } from '@iconify/react';
import refreshFill from "@iconify/icons-eva/refresh-fill";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useAuth } from "src/hooks/useAuth.hook";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}60`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

export const PaymentGatewayList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const hasFinanceConfigAccess = useAccess(FEATURE.FINANCE, "FA");
  const paymentGatewayCreateAccess = useAccess(FEATURE.PAYMENTGATEWAY, "C");
  const paymentGatewayReadOnly = useAccess(FEATURE.PAYMENTGATEWAY, "R");
  
  // Check if Grand Parent Company is selected (teamId matches user.company.id)
  const isGrandParentCompanySelected = useMemo(() => {
    return String(teamId) === String(user?.company?.id);
  }, [teamId, user?.company?.id]);
  
  // For GP users with Finance FA access, allow create even if PaymentGateway create access is not in backend
  // If GP is selected and has Finance FA access, they can create payment gateways
  const hasCreateAccess = hasFinanceConfigAccess && isGrandParentCompanySelected;
  const isReadOnly = hasFinanceConfigAccess && paymentGatewayReadOnly;
  const { data: paymentGateways, isFetching, refetch } = useQuery({
    queryKey: queryKeys.paymentGatewayDetails(),
    queryFn: fetchWithError,
    select: selector,
  });
  const { open, data } = useSelector(({ dialog }) => dialog);
  const handleClose = () => dispatch(dialogSlice.actions.setDialog(false));
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState('');

  // Redirect if user doesn't have Finance FA access (grand parent company access)
  useEffect(() => {
    if (!hasFinanceConfigAccess) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized - Grand Parent Company Access Required" })
      );
      navigate("/dashboard");
    }
  }, [hasFinanceConfigAccess, dispatch, navigate]);

  const rowOrCellClickRedirect = ({ id }) => {
    if(isReadOnly) return;
    navigate(`/payment-gateways/${id}`);
  };

  useEffect(() => {
    if (!isFetching && paymentGateways) {
      // Ensure paymentGateways is an array
      const gatewaysArray = Array.isArray(paymentGateways) ? paymentGateways : [];
      // Ensure each row has an id property
      const dataWithIds = gatewaysArray.map((gateway) => ({
        id: gateway.id,
        ...gateway
      }));
      setTableData(dataWithIds);
    } else if (!isFetching && !paymentGateways) {
      setTableData([]);
    }
  }, [isFetching, paymentGateways]);

  const handleSearching = (event) => {
    const dataToSearchFrom = Array.isArray(paymentGateways) ? paymentGateways : [];
    const searchTerm = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchTerm);
    
    if (searchTerm) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "name",
            "gateway_type",
            "currency",
            "country",
            "domain",
            "key_id"
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) => 
              value ? value.toString().toLowerCase() : ""
            );
          
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchTerm)
          );
          
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, {
              id: currentObj.id,
              ...currentObj
            }];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else {
      setTableData(paymentGateways?.map(gateway => ({
        id: gateway.id,
        ...gateway
      })) ?? []);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <Page
      {...(hasCreateAccess && { path: `/payment-gateways/new` })}
      actionButtonText="Add New Payment Gateway"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Manage Payment Gateways
          </Typography>
          {searchValue && (
            <Chip
              label={`${tableData?.length || 0} results`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Stack>
      
      <StyledListCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search payment gateways"
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <BaseTable
          rows={tableData ?? []}
          isLoading={isFetching}
          columns={paymentGatewayListColumns(isReadOnly)}
          showExportCsvBtn={false}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={10}
          isReadOnly={isReadOnly}
          getRowId={(row) => row.id}
          height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              height: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              height: '52px !important',
            }
          }}
        />
      </StyledListCard>
    </Page>
  );
};

